/**
 * chipShownMemory — IJ-239 · Chip variety persistence (2026-08-01).
 * ─────────────────────────────────────────────────────────────────
 *
 * Session-scoped store of chip labels the user has already been
 * shown for a given partial. Consumed by useTypeahead.js to inject
 * `user_ctx.previously_shown_chips` into the next classifier call.
 *
 * Contract:
 *   • Per-partial store · one bucket per normalised partial string.
 *   • Up to `MAX_ROUNDS_PER_PARTIAL` (6) previous rounds retained;
 *     oldest round drops on write.
 *   • Read returns the flat de-duplicated list of labels the user
 *     has already seen for the given partial · lower-cased before
 *     the compare · original casing preserved on the way out.
 *   • Storage is `sessionStorage` — cleared on tab close, isolated
 *     per browser tab.
 *   • All three exports are pure of side effects on the input args.
 *
 * Canadian English throughout.
 */

const SHOWN_KEY = 'si_chip_shown_v1';
const MAX_ROUNDS_PER_PARTIAL = 6;

const normalise = (s) => String(s || '').toLowerCase().trim();

function readStore() {
  if (typeof sessionStorage === 'undefined') return {};
  try {
    const raw = sessionStorage.getItem(SHOWN_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return (parsed && typeof parsed === 'object') ? parsed : {};
  } catch {
    return {};
  }
}

function writeStore(store) {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(SHOWN_KEY, JSON.stringify(store));
  } catch {
    /* storage full — silently ignore */
  }
}

/**
 * Append a new chip set to the store under the given partial.
 * `chipLabels` may be any iterable of strings; falsy values are
 * skipped. The store keeps the most-recent MAX_ROUNDS_PER_PARTIAL
 * rounds per partial.
 */
export function recordShownChips(partial, chipLabels) {
  const key = normalise(partial);
  if (!key) return;
  if (!Array.isArray(chipLabels)) return;
  const cleaned = chipLabels
    .map((c) => String(c || '').trim())
    .filter((c) => c.length > 0);
  if (cleaned.length === 0) return;
  const store = readStore();
  const rounds = Array.isArray(store[key]) ? store[key] : [];
  rounds.push(cleaned);
  while (rounds.length > MAX_ROUNDS_PER_PARTIAL) rounds.shift();
  store[key] = rounds;
  writeStore(store);
}

/**
 * Return the flat de-duplicated list of chip labels the user has
 * seen for the given partial across all retained rounds. Preserves
 * the ORIGINAL casing of the first occurrence; dedup is
 * case-insensitive.
 */
export function getShownChips(partial) {
  const key = normalise(partial);
  if (!key) return [];
  const store = readStore();
  const rounds = Array.isArray(store[key]) ? store[key] : [];
  const seen = new Set();
  const out = [];
  rounds.forEach((round) => {
    if (!Array.isArray(round)) return;
    round.forEach((label) => {
      const nk = normalise(label);
      if (!nk) return;
      if (seen.has(nk)) return;
      seen.add(nk);
      out.push(label);
    });
  });
  return out;
}

/**
 * Clear all shown-chip memory (used by tests and on logout).
 */
export function clearShownChips() {
  if (typeof sessionStorage === 'undefined') return;
  try { sessionStorage.removeItem(SHOWN_KEY); } catch { /* noop */ }
}

export const CHIP_SHOWN_CONSTANTS = Object.freeze({
  SHOWN_KEY,
  MAX_ROUNDS_PER_PARTIAL,
});
